import { Exception } from '@inter-ikea-kompis/exceptions';
import type {
	IDexfSettingsLocalisation,
	ILocalizedPriceSettings,
	IPriceFormat
} from '@inter-ikea-kompis/types';

const THOUSAND_SEPERATOR_REGEXP = /(\d+)(\d{3})/;

export type IPriceFormatDexfSettings = {
	localisation: Pick<
		IDexfSettingsLocalisation,
		| 'currencyCode'
		| 'currencySymbol'
		| 'noValueDecimalsString'
		| 'thousandSeparatorEnabled'
		| 'thousandSeparator'
		| 'numberOfDecimals'
		| 'replaceNoValueDecimals'
		| 'decimalSeparator'
		| 'currencyPosition'
		| 'symbolSpacing'
	>;
};

/**
 * This is an internal utility, not meant to be used by planners.
 *
 * Handles formatting of prices.
 */
export default class PriceFormatterUtility {
	/**
	 * Formats a price.
	 * Make sure that you add a dir="ltr" attribute to the HTML, so that '1 400' wont get reversed in morocco.
	 *
	 * @param amount Price amount.
	 * @param priceSettingsOrDexfPriceSettings Price settings from LocalizationService or DEXF settings.
	 * @returns Formatted price with currency and spacing. The price formats are joinable by doing: priceFormat.map(price => price.value).join('').
	 */
	public static format(
		amount: number,
		priceSettingsOrDexfPriceSettings: ILocalizedPriceSettings | IPriceFormatDexfSettings
	): IPriceFormat[] {
		const localisation =
			'localisation' in priceSettingsOrDexfPriceSettings
				? priceSettingsOrDexfPriceSettings.localisation
				: priceSettingsOrDexfPriceSettings;

		if (typeof amount !== 'number' || isNaN(amount)) {
			throw new Exception('Failed to format price. Invalid number.');
		}
		const numberOfDecimals = localisation.numberOfDecimals;
		if (isNaN(numberOfDecimals) || numberOfDecimals === null) {
			throw new Exception('priceSettings.numberOfDecimals is not a number.');
		}

		const noValueDecimalsString =
			localisation.noValueDecimalsString === ' ' ? '' : localisation.noValueDecimalsString;
		const fixedAmount = amount.toFixed(numberOfDecimals).split('.'); // Since the number of trailing zeroes is set with the toFixed, the values cannot be integer
		const fraction: string = fixedAmount.length === 2 ? fixedAmount[1] : `${0}`;

		// Thousand separator
		let integerString = fixedAmount[0];
		if (localisation.currencyCode === 'INR') {
			integerString = this.formatIndianThousandSeperator(integerString);
		} else if (localisation.thousandSeparatorEnabled && integerString.length > 3) {
			integerString = this.formatThousandSeperator(integerString, localisation.thousandSeparator);
		}

		// Decimals
		let decimalArray: string[] = [];
		if (
			localisation.replaceNoValueDecimals &&
			typeof noValueDecimalsString === 'string' &&
			Number(fraction) === 0
		) {
			decimalArray = [noValueDecimalsString];
		} else if (localisation.decimalSeparator && numberOfDecimals !== 0) {
			decimalArray = [localisation.decimalSeparator, fraction];
		}

		// List of parts in order
		const priceWithCurrency: IPriceFormat[] = [
			{
				type: 'integerPart',
				value: integerString
			}
		];

		if (decimalArray.length > 0) {
			priceWithCurrency.push({
				type: 'decimalPart',
				value: decimalArray.join('')
			});
		}

		if (localisation.currencyPosition === 'before') {
			if (localisation.symbolSpacing) {
				priceWithCurrency.unshift({
					type: 'spacing',
					value: ' '
				});
			}
			priceWithCurrency.unshift({
				type: 'currency',
				value: localisation.currencySymbol
			});
		} else {
			if (localisation.symbolSpacing) {
				priceWithCurrency.push({
					type: 'spacing',
					value: ' '
				});
			}
			priceWithCurrency.push({
				type: 'currency',
				value: localisation.currencySymbol
			});
		}

		return priceWithCurrency;
	}

	/**
	 * Returns Indian price with thousand separator.
	 *
	 * @param integerString Price integer part (no decimals).
	 * @returns Indian price with thousand separator.
	 */
	private static formatIndianThousandSeperator(integerString: string): string {
		const result: string[] = [];
		let thousand = '';

		if (integerString.length > 3) {
			thousand = ',' + integerString.substring(integerString.length - 3);
			integerString = integerString.substring(0, integerString.length - 3);

			while (integerString.length > 2) {
				result.unshift(integerString.substring(integerString.length - 2));
				integerString = integerString.substring(0, integerString.length - 2);
			}
		}

		if (integerString.length > 0) {
			result.unshift(integerString);
		}

		return result.join(',') + thousand;
	}

	/**
	 * Returns price with thousand separator.
	 *
	 * @param integerString Price integer part (no decimals).
	 * @param thousandSeparator Thousand separator.
	 * @returns Price with thousand separator.
	 */
	private static formatThousandSeperator(integerString: string, thousandSeparator: string): string {
		const separator = thousandSeparator || ' ';
		while (THOUSAND_SEPERATOR_REGEXP.test(integerString)) {
			integerString = integerString.replace(THOUSAND_SEPERATOR_REGEXP, '$1' + separator + '$2');
		}

		return integerString;
	}
}
